import { useEffect } from 'react';
import { useLocation } from '@remix-run/react';
import { plansRoute } from '~/services/plugins/web-products';
import logger from '~/services/logger';
import { safeParseJSON } from '~/services/layout/layout';

const SCREEN_LIMIT = 5;

/**
 * Retrieves the screen navigation history from localStorage.
 *
 * @returns string[] of screen paths, limited to the most recent SCREEN_LIMIT entries
 */
export const getHistory = () => {
  const screensHistory =
    safeParseJSON(localStorage?.getItem('screensHistory') || '[]') || [];
  // Only slice if we exceed the limit
  if (screensHistory.length <= SCREEN_LIMIT) {
    return screensHistory;
  }
  return screensHistory.slice(screensHistory.length - SCREEN_LIMIT);
};

export const setHistory = (newArr: string[]) => {
  localStorage.setItem('screensHistory', JSON.stringify(newArr));
};

/**
 * Adds a new screen path to the navigation history.
 * Prevents duplicate consecutive entries and enforces history limits.
 *
 * @param {string} path - The screen path (pathname + search) to add to history
 */
export const addToHistory = (path: string) => {
  const limitedHistory = getHistory();

  // Don't add duplicate entries - if the last entry is the same as current, skip
  if (
    limitedHistory.length > 0 &&
    limitedHistory[limitedHistory.length - 1] === path
  ) {
    return;
  }

  limitedHistory.push(path);

  // Keep history aligned with browser history length, but cap at SCREEN_LIMIT
  const maxHistoryLength = Math.min(window.history.length, SCREEN_LIMIT);
  const trimmedHistory = limitedHistory.slice(-maxHistoryLength);

  setHistory(trimmedHistory);
};

/**
 * Removes a specific path from the end of navigation history if it matches the last entry.
 * Used to clean up locked screen paths that shouldn't remain in navigation history.
 *
 * @param {string} currentPath - The screen path to remove if it's the last history entry
 * @returns {string[]} The updated history array after removal
 */
export const removeCurrentFromHistory = (currentPath: string) => {
  const limitedHistory = getHistory();

  // Remove the current path if it's the last entry
  if (
    limitedHistory.length > 0 &&
    limitedHistory[limitedHistory.length - 1] === currentPath
  ) {
    limitedHistory.pop();
    setHistory(limitedHistory);
  }

  return limitedHistory;
};

const ignorePaths = [plansRoute, '/lock'];

/**
 * React hook that tracks screen navigation history in localStorage.
 * Automatically adds visited screens to history, with smart handling for screens that have hooks
 * (like parent lock validation) and ignores certain paths like /lock.
 *
 * @param {boolean} [hasScreenHooks] - Optional flag indicating if the current screen has hooks
 *                                   that might cause redirects. If true, history tracking is
 *                                   deferred to useScreenHooks to handle properly.
 */
export const useScreensHistory = (hasScreenHooks?: boolean) => {
  const location = useLocation();

  useEffect(() => {
    try {
      if (ignorePaths.includes(location.pathname)) {
        return;
      }

      // Don't add screens with hooks to history here - useScreenHooks will handle it
      if (hasScreenHooks) {
        return;
      }

      const currentPath = location.pathname + location.search;
      addToHistory(currentPath);
    } catch (error) {
      // log error but don't block navigation
      logger.error(
        `useScreensHistory error: failed to add ${
          location.pathname + location.search
        } to navigation history`
      );
    }
  }, [location.pathname, location.search, hasScreenHooks]);
};
